import { Toast } from "primereact/toast";
import { useContext, useEffect, useRef, useState } from "react";
import Spinner from "../../components/spinner/spinner";
import { useFormik } from "formik";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import styles from "./ItemAndStoreSearchScreen.module.scss";
import { Button } from "primereact/button";
import * as constants from "../../constants/constants";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import * as Yup from "yup";
import { appInsights } from "../../AppInsights";
import LoginUserDataContext from "../../store/login";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import * as actionCreators from "../../store/action-creators";
import { sortData } from "../../constants/dataSort";
import {
  itemXrefColumns,
  shipToXrefColumns
} from "../../constants/table-columns/tableColumns";
import { ItemAndStoreService } from "../../services/ItemAndStoreService";

const ItemAndStoreSearchScreen = ({ screenName }) => {
  const toast = useRef(null);
  const toastTopRight = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageRows, setCurrentPageRows] = useState(10);
  const [userData, setUserData] = useContext(LoginUserDataContext);
  const [defaultRegion, setDefaultRegion] = useState("");
  const [customerName, setCustomerName] = useState([]);
  const formRef = useRef(null);
  const [disableFields, setDisableFields] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [itemOrStoreDetails, setItemOrStoreDetails] = useState([]);
  const dispatch = useDispatch();
  const { getRegions, getFunctionMasterData } = bindActionCreators(
    actionCreators,
    dispatch
  );
  const customer = useSelector((state) => {
    return sortData("customerName", state.orderReducer.masterData?.customers);
  });
  const regions = useSelector((state) => {
    const data = state?.orderReducer?.regions;
    const sortedData = sortData("regionNameAbbr", data);
    return sortedData;
  });
  const fetchMasterData = async () => {
    try {
      if (customer?.length) return;
      await getFunctionMasterData();
    } catch (error) {
      setShowSpinner(false);
      appInsights.trackException({
        exception: error,
        properties: {
          additionalInfo: "Exception while fetching Function Master data"
        },
        measurements: {
          severityLevel: 2
        }
      });
    }
  };
  const fetchAllRegions = async () => {
    try {
      if (regions?.length) return;
      await getRegions();
    } catch (error) {
      setShowSpinner(false);
      appInsights.trackException({
        exception: error,
        properties: {
          additionalInfo: "Exception while fetching Regions data"
        },
        measurements: {
          severityLevel: 2
        }
      });
    }
  };

  useEffect(() => {
    fetchMasterData();
    fetchAllRegions();
    appInsights.trackPageView({
      name: `${screenName} Item and store screen visited`
    });
  }, []);

  useEffect(() => {
    if (!regions?.length || !customer?.length) return;
    if (userData?.regionId != null) {
      let filteredRegion = regions?.filter(
        (item) => item?.regionId === userData?.regionId
      );
      formik.setFieldValue(
        "region",
        filteredRegion[0] ? filteredRegion[0] : regions[0]?.regionNameAbbr
      );
      setDefaultRegion(filteredRegion[0]);
      const filteredCustomers = customer?.filter(
        (cust) => cust?.regionId == filteredRegion[0]?.regionId
      );
      setCustomerName(filteredCustomers);
    } else {
      formik.setFieldValue("region", regions[0]?.regionNameAbbr);
      const filteredCustomers = customer?.filter(
        (cust) => cust?.regionId == regions[0]?.regionId
      );
      setCustomerName(filteredCustomers);
    }
    setShowSpinner(false);
  }, [regions, customer]);

  const initialValues = {
    region: defaultRegion ? defaultRegion : regions[0],
    id: "",
    customerId: "",
    customerName: null
  };

  const validationSchema = Yup.object().shape({
    region: Yup.object().nullable().required("Region is required"),
    id: Yup.string().nullable(),
    customerId: Yup.string().nullable(),
    customerName: Yup.object().nullable()
  });

  const fetchItemOrStoreDetails = async (values) => {
    try {
      setShowSpinner(true);
      const onlyRegionSelected =
        values?.region &&
        !values?.customerName?.customerId &&
        !values?.customerId &&
        !values?.id;

      values = {
        regionId: values?.region?.regionId,
        customerId: values?.customerName?.customerId,
        ...(screenName?.toLocaleLowerCase() == "item"
          ? {
            buyerItemId: values?.customerId,
            sellerItemId: values?.id
          }
          : {
            buyerShipToId: values?.id,
            sellerShipToId: values?.customerId
          })
      };
      if (onlyRegionSelected) {
        values.topN = 100;
      }
      let result = await ItemAndStoreService(values, `${screenName}`);
      setItemOrStoreDetails(result);
      setShowSpinner(false);
    } catch (error) {
      setShowSpinner(false);
      setItemOrStoreDetails([]);
      toast?.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.message,
        life: 4000
      });
      appInsights.trackException({
        exception: error,
        properties: {
          additionalInfo: `Exception while Fetching ${screenName} details`
        },
        measurements: {
          severityLevel: 2
        }
      });
    }
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: (values, { resetForm }) => {
      setCurrentPage(0);
      setCurrentPageRows(10);
      fetchItemOrStoreDetails(values);
      setShowSpinner(true);
    },
    validateOnChange: true,
    validateOnBlur: true
  });

  return (
    <>
      {showSpinner && <Spinner />}
      <div className="grid align-items-end">
        <div className="col-12 ">
          <form onSubmit={formik.handleSubmit} ref={formRef}>
            <div className="grid p-fluid">
              {/* Region Dropdown */}
              <div className="col-12 md:col-3">
                <div className={styles?.formLabelContainer}>
                  <label htmlFor="region">
                    <span className={styles?.formLabel}>Region</span>
                  </label>
                </div>
                <Dropdown
                  id="region"
                  name="region"
                  filter
                  options={regions}
                  optionLabel="regionNameAbbr"
                  placeholder="Region"
                  value={formik.values.region}
                  onChange={async (e) => {
                    formik.setFieldTouched("region", true);
                    formik.setFieldValue("region", e?.value);
                    formik.setFieldValue("customerName", null);
                    formik.setFieldValue("id", "");
                    formik.setFieldValue("customerId", "");
                    const filteredCustomers = customer?.filter(
                      (cust) => cust?.regionId === e.value?.regionId
                    );
                    if (filteredCustomers) {
                      setCustomerName(filteredCustomers);
                    }
                  }}
                  className={styles.dropdown}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.region && formik.errors.region && (
                  <small className="p-error">{formik.errors.region}</small>
                )}
              </div>

              {/* Customer Dropdown */}
              <div className="col-12 md:col-3">
                <div className={styles?.formLabelContainer}>
                  <label htmlFor="customerName">
                    <span className={styles?.formLabel}>Customer</span>
                  </label>
                </div>
                <Dropdown
                  id="customerName"
                  name="customerName"
                  filter
                  options={customerName}
                  optionLabel="customerName"
                  placeholder="Select Customer"
                  value={formik.values.customerName}
                  onChange={(e) => {
                    formik.setFieldTouched("customerName", true);
                    formik.setFieldValue("customerName", e?.value);
                  }}
                  className={styles.dropdown}
                  onBlur={formik.handleBlur}
                />
              </div>

              {/* Seller/Buyer ID */}
              <div className="col-12 md:col-3">
                <div className={styles.formLabelContainer}>
                  <label htmlFor="id">
                    <span className={styles.formLabel}>
                      {screenName.toLowerCase() === "item" ? "Seller" : "Buyer"}{" "}
                      {screenName.toLowerCase() === "item" ? "Item" : "ship to"}{" "}
                      Id
                    </span>
                  </label>
                </div>
                <InputText
                  id="id"
                  placeholder={`Enter ${screenName.toLowerCase() === "item" ? "Seller" : "Buyer"
                    } ${screenName.toLowerCase() === "item" ? "item" : "ship to"
                    } Id`}
                  value={formik.values.id}
                  onChange={(e) => formik.setFieldValue("id", e?.target.value)}
                  className={styles.dropdown}
                />
              </div>

              {/* Buyer/Seller ID */}
              <div className="col-12 md:col-3">
                <div className={styles.formLabelContainer}>
                  <label htmlFor="customerId">
                    <span className={styles.formLabel}>
                      {screenName.toLowerCase() === "item" ? "Buyer" : "Seller"}{" "}
                      {screenName.toLowerCase() === "item" ? "Item" : "ship to"}{" "}
                      Id
                    </span>
                  </label>
                </div>
                <InputText
                  id="customerId"
                  placeholder={`Enter ${screenName.toLowerCase() === "item" ? "Buyer" : "Seller"
                    } ${screenName.toLowerCase() === "item" ? "item" : "ship to"
                    } Id`}
                  value={formik.values.customerId}
                  onChange={(e) =>
                    formik.setFieldValue("customerId", e?.target.value)
                  }
                  className={styles.dropdown}
                />
              </div>
            </div>

            {/* Buttons (Aligned Right) */}
            <div
              className="flex justify-content-end flex-wrap gap-3"
              style={{ marginTop: "0rem", marginBottom: "0.3rem" }}
            >
              <div className="flex align-items-center justify-content-center">
                <Button
                  label="Search"
                  type="submit"
                  size="medium"
                  className={styles?.primaryBtn}
                />
              </div>
              <div className="flex align-items-center justify-content-center">
                <Button
                  label="Reset"
                  type="button"
                  size="medium"
                  onClick={() => {
                    formik.setValues({
                      region: defaultRegion || regions[0],
                      id: "",
                      customerId: "",
                      customerName: null
                    });
                    const regionToFilter =
                      defaultRegion?.regionId ?? regions[0]?.regionId;
                    const filteredCustomers = customer?.filter(
                      (cust) => cust?.regionId === regionToFilter
                    );
                    if (filteredCustomers) {
                      setCustomerName(filteredCustomers);
                    }
                    setItemOrStoreDetails([]);
                    formik.setTouched({}, false);
                    setDisableFields(false);
                  }}
                  className={styles?.resetBtn}
                />
              </div>
            </div>

            {/* Info Message */}
            {/* <div className="flex justify-content-end">
                <div className={styles.recordLimit}>
                  <span>
                    <i
                      className={`${styles.recordLimit} pi pi-info-circle p-0`}
                      style={{ fontSize: "10px", marginBottom: "0.5rem" }}
                    ></i>{" "}
                    Fetching latest 100 records only
                  </span>
                </div>
              </div> */}
          </form>
        </div>
      </div>

      <div className="mt-4">
        <DataTable
          value={itemOrStoreDetails || []}
          tableStyle={{ minWidth: "50rem" }}
          scrollable
          paginator={constants.paginator}
          first={currentPage}
          rows={currentPageRows}
          onPage={(e) =>{setCurrentPage(e.first); setCurrentPageRows(e?.rows)}}
          rowsPerPageOptions={constants.rowsPerPageOptions}
          paginatorTemplate={constants.paginatorTemplate}
          currentPageReportTemplate={constants.currentPageReportTemplate}
          emptyMessage={constants.emptyMessage}
          dataKey="id"
          scrollHeight="30vw"
        >
          {screenName.toLocaleLowerCase() == "item"
            ? itemXrefColumns.map((col, index) => (
              <Column
                key={index}
                field={col.field}
                header={col.header}
                sortable={col.sort}
                style={{ minWidth: col.minWidth }}
                headerStyle={{ minWidth: col.minWidth }}
                body={col.value || null}
              />
            ))
            : shipToXrefColumns.map((col, index) => (
              <Column
                key={index}
                field={col.field}
                header={col.header}
                sortable={col.sort}
                style={{ minWidth: col.minWidth }}
                headerStyle={{ minWidth: col.minWidth }}
                body={col.value || null}
              />
            ))}
        </DataTable>
      </div>

      <Toast ref={toast} position="center" />

      <Toast ref={toastTopRight} position="top-right" />
    </>
  );
};
export default ItemAndStoreSearchScreen;
